import React, { useRef, useState } from 'react'
import Autocomplete from '@mui/material/Autocomplete'
import Avatar from '@mui/material/Avatar'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import FormControl from '@mui/material/FormControl'
import InputAdornment from '@mui/material/InputAdornment'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { Checkbox, ListItemText, OutlinedInput } from '@mui/material'
import TipTap from '../../../../components/TipTap/TipTap'

const AddAgendaPopup = ({
  staffOptions,
  setAgendaItems,
  onClose,
  agendaItems,
  meetingType,
  submitAdditionalActions,
  existingAgenda,
  existingAgendaPosition,
  setChangesPresent,
}) => {
  const [title, setTitle] = useState(existingAgenda?.title ?? '')
  const [description, setDescription] = useState(
    existingAgenda?.description ?? ''
  )
  const [lead, setLead] = useState(existingAgenda?.lead ?? [])
  const [cqcQualityStatement, setCqcQualityStatement] = useState(
    existingAgenda?.cqcQualityStatement ?? []
  )
  const [estimatedTime, setEstimatedTime] = useState(
    existingAgenda?.estimatedTime ?? ''
  )
  const [files, setFiles] = useState(existingAgenda?.files ?? null)
  const fileInputRef = useRef(null)

  const isDuplicateItem = existingAgenda
    ? agendaItems.filter((x) => x.title === title).length > 1
    : agendaItems.some((x) => x.title === title)

  const handleSubmit = (e) => {
    e.preventDefault()
    setAgendaItems((prev) =>
      existingAgenda
        ? agendaItems.map((x, i) =>
            i === existingAgendaPosition
              ? {
                  ...x,
                  title,
                  description,
                  lead,
                  cqcQualityStatement,
                  estimatedTime,
                  files,
                }
              : x
          )
        : [
            ...prev,
            { title, description, lead, cqcQualityStatement, estimatedTime },
          ]
    )
    setFiles(null)
    onClose(title)
    submitAdditionalActions && submitAdditionalActions()
    setChangesPresent && setChangesPresent(true)
  }

  const cqcOptions = ['Safe', 'Effective', 'Caring', 'Responsive', 'Well-Led']
  return (
    <Stack gap={1} component="form" onSubmit={handleSubmit}>
      <Typography variant="title3">Add Agenda Item</Typography>
      <TextField
        label="Title"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        required
        name="agenda-item-title"
        error={isDuplicateItem}
        helperText={
          isDuplicateItem && 'An agenda item with this title already exists'
        }
      />
      <TextField
        label="Description"
        value={description}
        multiline
        minRows={3}
        onChange={(e) => setDescription(e.target.value)}
      />
      <Autocomplete
        value={lead}
        options={staffOptions ?? []}
        multiple
        getOptionKey={(option) => option._id}
        getOptionLabel={(option) => option?.firstName + ' ' + option?.lastName}
        renderTags={(options) => (
          <Box display="flex" gap={1}>
            {options.map((opt) => {
              if (opt) {
                return (
                  <Box
                    display="flex"
                    gap={1}
                    key={opt?._id ?? 1}
                    border="solid 1px rgba(0,0,0,0.24)"
                    borderRadius={1}
                    padding={1}
                  >
                    <Avatar
                      src={opt?.photograph}
                      sx={{ width: 24, height: 24 }}
                    />
                    <Typography>
                      {opt?.firstName} {opt?.lastName}
                    </Typography>
                  </Box>
                )
              }
            })}
          </Box>
        )}
        renderOption={(props, option) => {
          return (
            <Box display="flex" gap={1} {...props}>
              <Avatar src={option?.photograph} sx={{ width: 24, height: 24 }} />
              <Typography>
                {option?.firstName} {option?.lastName}
              </Typography>
            </Box>
          )
        }}
        onChange={(e, value) => setLead(value)}
        renderInput={(params) => <TextField {...params} label="Lead" />}
      />
      {meetingType === 'Governance' && (
        <Autocomplete
          options={cqcOptions}
          value={cqcQualityStatement}
          onChange={(e, value) => setCqcQualityStatement(value)}
          multiple
          renderInput={(params) => (
            <TextField {...params} label="CQC Question" />
          )}
        />
      )}
      <TextField
        type="number"
        label="Estimated Time"
        onChange={(e) =>
          e.target.value >= 0 && setEstimatedTime(e.target.value)
        }
        defaultValue={estimatedTime}
        name="meeting-estimated-time"
        InputProps={{
          endAdornment: <InputAdornment position="end">minutes</InputAdornment>,
        }}
        required
      />
      <TextField
        type="file"
        label="Attachments"
        defaultValue={files}
        onChange={(e) => setFiles(e.target.files)}
        InputLabelProps={{
          shrink: true,
        }}
        inputProps={{
          multiple: true,
        }}
      />
      <Button
        variant="contained"
        color="success"
        type="submit"
        disabled={isDuplicateItem}
      >
        Submit
      </Button>
    </Stack>
  )
}

export default AddAgendaPopup
